import { Microphone, Paperclip } from "@phosphor-icons/react";
import clsx from "clsx";

import RenderIf from "common/components/RenderIf";
import { Tag } from "common/components/ui/Tag";

import useInboxDraftStore from "../../hooks/useInboxDraftStore";
import useInboxTypingStore from "../../hooks/useInboxTypingStore";
import { getTypingString } from "../../utils";
import HighlightedText from "../HighlightedText";

import type { ReactNode } from "react";
import type { Conversation } from "../../types";

interface MessageDetailsProps {
  conversation: Conversation;
  isMine: boolean;
}

export default function MessageDetails({
  conversation,
  isMine,
}: MessageDetailsProps) {
  const {
    last_message: lastMessage,
    unread_count: unreadCount,
    participants,
    conversation_urn: conversationUrn,
    highlight,
  } = conversation;
  const { deleted: isDeleted, inmail: isInmail, sender, body } = lastMessage;

  const { drafts } = useInboxDraftStore();
  const { typingState } = useInboxTypingStore();

  const draftMessage = drafts[conversationUrn];
  const typingAccountIds = typingState[conversationUrn];
  const isGroupConversation = participants.length > 1;

  if (typingAccountIds) {
    return (
      <span className="line-clamp-1 break-all text-start text-xs text-black-500">
        {getTypingString(typingAccountIds, participants) || "Typing"}
        ...
      </span>
    );
  }
  if (draftMessage) {
    return (
      <div className="inline-flex flex-row items-center gap-0.5 w-full">
        <span className="text-purple-600 text-xs-bold">Draft:</span>
        <span className="text-xs text-black-500 truncate">{draftMessage}</span>
      </div>
    );
  }

  if (!body) {
    const isAudio = "audio" in lastMessage;

    return (
      <div
        className={clsx([
          "flex flex-row items-center gap-0.5",
          unreadCount
            ? "text-xs-bold text-black-950"
            : "text-xs text-black-500",
        ])}
      >
        <RenderIf condition={isMine}>
          <span className="text-xs text-black-500">You:</span>
        </RenderIf>

        {isAudio ? <Microphone /> : <Paperclip />}

        <span className="line-clamp-1 break-all text-start italic">
          {isAudio ? "Sent a voice message" : "Sent an attachment"}
        </span>
      </div>
    );
  }

  let message: ReactNode = isMine ? `You: ${body}` : body;

  if (isDeleted) {
    message = "This message has been deleted";
  } else if (highlight) {
    let highlightedText: string;

    if ("body" in highlight) {
      const searchResult = highlight.body[0];
      const messageParts = searchResult.split(" ");
      const highlightIndex = messageParts.findIndex((text) =>
        text.includes("<em>"),
      );
      const sliceStart = highlightIndex - 4;
      const sliceEnd = highlightIndex + 4;

      highlightedText = messageParts
        .slice(
          sliceStart < 0 ? 0 : sliceStart,
          Math.min(sliceEnd, messageParts.length),
        )
        .join(" ");
    } else if ("subject" in highlight) {
      highlightedText = `Subject: ${highlight.subject}`;
    }
    if (highlightedText) {
      message = (
        <HighlightedText highlightClassname="text-xs-bold">
          {highlightedText}
        </HighlightedText>
      );
    }
  }

  if (isInmail) {
    return (
      <div className="flex flex-row items-center gap-1">
        <Tag size="sm" variant="secondary">
          InMail
        </Tag>
        <span
          className={clsx([
            "line-clamp-1 text-start text-xs text-black-500",
            unreadCount && "max-w-[40ch]",
          ])}
        >
          {message}
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-row justify-between">
      <div className="flex flex-row items-center gap-1 text-start leading-normal overflow-hidden">
        {/* Render group name if applicable */}
        <RenderIf condition={isGroupConversation && !isMine}>
          <span className="text-nowrap text-xs text-black-500">
            {sender.full_name}:
          </span>
        </RenderIf>

        {/* Message content */}
        <span
          className={clsx([
            "line-clamp-1 w-full text-xs text-black-500",
            unreadCount
              ? "max-w-[40ch] text-xs-bold text-black-950"
              : "max-w-[45ch]",
            isDeleted && "pr-1 italic",
          ])}
        >
          {message}
        </span>
      </div>
    </div>
  );
}
